import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, e as emitEvent, B as debounce, u as updateElementStyle, d as isNumber, f as isUnset, l as logDeprecatedEventOnce, C as focusElement, n as numToRem } from './utils.js';
import { d as defineCustomElement$1 } from './cl-scroll-wrapper2.js';

// Constants
const component = 'cl-dropdown';
const dropdownConstants = {
  VARIANT: {
    INLINE: 'inline',
    LIST: 'list',
    MENU: 'menu',
  },
};
// Type-validators
const dropdownValidator = {
  variant: createTypeValidator(dropdownConstants.VARIANT),
};

const clDropdownCss = ".sc-cl-dropdown-h{--dropdown-width:auto;--dropdown-z-index:var(--z-index-2);background-color:rgba(var(--color-neutral-main));border:var(--border-width) solid transparent;border-radius:var(--border-radius-interactive-small);box-shadow:var(--elevation-2);color:rgba(var(--color-neutral-900));display:flex;flex-direction:column;font-family:var(--font-family-ui);font-size:var(--font-size-body-2);font-weight:var(--font-weight-400);min-width:-moz-fit-content;min-width:fit-content;outline:none;overflow:hidden;padding:var(--spacing-2) var(--spacing-1);position:absolute;width:var(--dropdown-width);z-index:var(--dropdown-z-index)}.sc-cl-dropdown-h,.sc-cl-dropdown-h *.sc-cl-dropdown,.sc-cl-dropdown-h *.sc-cl-dropdown::before,.sc-cl-dropdown-h *.sc-cl-dropdown::after,.sc-cl-dropdown-h .sc-cl-dropdown-s>*,.sc-cl-dropdown-h .sc-cl-dropdown-s *,.sc-cl-dropdown-h .sc-cl-dropdown-s *::before,.sc-cl-dropdown-h .sc-cl-dropdown-s *::after{box-sizing:border-box}.dropdown.sc-cl-dropdown-h{--dropdown-speed-open:200ms;--dropdown-speed-close:125ms;max-height:var(--custom-dropdown-max-height, var(--dropdown-max-height, calc(7 * var(--size-4))));transform-origin:center top}.dropdown--opening.sc-cl-dropdown-h{animation:expand var(--dropdown-speed-open) linear}.dropdown--opening.sc-cl-dropdown-h .dropdown__wrapper.sc-cl-dropdown{animation:fade-in calc(0.5 * var(--dropdown-speed-open)) linear}.dropdown--opened.sc-cl-dropdown-h{opacity:1;transform:scaleY(1)}.dropdown--closing.sc-cl-dropdown-h{animation:collapse var(--dropdown-speed-close) cubic-bezier(0.69, 0, 0.21, 1)}.dropdown--closing.sc-cl-dropdown-h .dropdown__wrapper.sc-cl-dropdown{animation:fade-out var(--dropdown-speed-close)}.dropdown--closed.sc-cl-dropdown-h{opacity:0;transform:scaleY(0)}.dropdown--menu.sc-cl-dropdown-h{--dropdown-max-height:100%;max-height:-moz-max-content;max-height:max-content}.dropdown--inline.sc-cl-dropdown-h{box-shadow:none;max-height:var(--custom-dropdown-max-height, var(--dropdown-max-height, calc(7 * var(--size-4))));position:relative;visibility:visible}.dropdown--full-width.sc-cl-dropdown-h{--dropdown-width:100%}@keyframes fade-out{from{opacity:1}to{opacity:0}}@keyframes fade-in{from{opacity:0}to{opacity:1}}@keyframes expand{0%{opacity:0;transform:scaleY(0)}20%{transform:scaleY(1.05)}50%{transform:scaleY(0.95)}60%{opacity:1;transform:scaleY(1.025)}90%{transform:scaleY(0.975)}100%{transform:scaleY(1)}}@keyframes collapse{from{opacity:1;transform:scaleY(1)}to{opacity:0;transform:scaleY(0)}}";

const ClDropdown = /*@__PURE__*/ proxyCustomElement(class ClDropdown extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clChange = createEvent(this, "clChange", 7);
    this.clCloseEnd = createEvent(this, "clCloseEnd", 7);
    this.clCloseStart = createEvent(this, "clCloseStart", 7);
    this.clOpenEnd = createEvent(this, "clOpenEnd", 7);
    this.clOpenStart = createEvent(this, "clOpenStart", 7);
    this.change = createEvent(this, "change", 7);
    this.closeEnd = createEvent(this, "closeEnd", 7);
    this.closeStart = createEvent(this, "closeStart", 7);
    this.openEnd = createEvent(this, "openEnd", 7);
    this.openStart = createEvent(this, "openStart", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Whether the dropdown is in the process of showing the open/close animation or not
     */
    this.isAnimating = false;
    /**
     * Function that handles getting the initially selected cl-dropdown-item elements,
     * and storing their values in the `selectedDropdownItems` array
     */
    this.getInitialSelectedElements = () => {
      const selectedDropdownItems = Array.from(this.element.querySelectorAll('cl-dropdown-item.dropdown-item--selected'));
      const selectedDropdownItemsValues = selectedDropdownItems.map(element => ({
        label: element.label,
        value: element.value,
      }));
      if (selectedDropdownItemsValues.length) {
        // Remove the selected state from pre-selected dropdown items, if the `multiple` property is set to false
        // and multiple dropdown-items are selected
        if (!this.multiple && selectedDropdownItemsValues.length > 1) {
          selectedDropdownItems.forEach(this.unsetSelectedAttribute);
          this.logger.error.attribute.multiple('Multiple selected dropdown-items detected/found while the "multiple" attribute is set to false.');
          return;
        }
        this.selectedDropdownItems = selectedDropdownItems;
        this.value = selectedDropdownItemsValues;
        this.valueState = selectedDropdownItemsValues;
      }
    };
    /**
     * Function that emits the correct animation event based on whether the dropdown is opening or closing
     */
    this.handleAnimationEvent = (event) => {
      const { animationName, type } = event;
      const isOpenEvent = animationName === 'expand';
      const isCloseEvent = animationName === 'collapse';
      switch (true) {
        case isOpenEvent && type === 'animationstart':
          emitEvent(this, 'clOpenStart', { component, originalEvent: event }, null);
          if (this.autoFocus) {
            this.focusDropdownItem(this.getFirstDropdownItemElement());
          }
          break;
        case isOpenEvent && type === 'animationend':
          this.element.classList.remove('dropdown--opening');
          this.element.classList.add('dropdown--opened');
          this.isAnimating = false;
          emitEvent(this, 'clOpenEnd', { component, originalEvent: event }, null);
          break;
        case isCloseEvent && type === 'animationstart':
          emitEvent(this, 'clCloseStart', { component, originalEvent: event }, null);
          break;
        case isCloseEvent && type === 'animationend':
          this.element.classList.remove('dropdown--closing');
          this.element.classList.add('dropdown--closed');
          this.isAnimating = false;
          emitEvent(this, 'clCloseEnd', { component, originalEvent: event }, null);
          break;
      }
    };
    /**
     * Function that clears the value of the dropdown-item filter, 500ms after the last input change
     */
    this.resetDropdownItemFilter = debounce(() => (this.dropdownItemFilter = ''), 500);
    this.dropdownItemFilter = '';
    this.selectedDropdownItems = [];
    this.valueState = [];
    this.autoFocus = false;
    this.closeOnSelect = false;
    this.fullWidth = false;
    this.maxHeight = undefined;
    this.multiple = false;
    this.open = false;
    this.value = [];
    this.variant = 'list';
  }
  /**
   * Finds and focuses the dropdown-item that matches the given `dropdownItemFilter` value
   */
  handleDropdownItemFilter(dropdownItemFilter) {
    const matchedDropdownItem = this.element.querySelector(`cl-dropdown-item:not(.dropdown-item--disabled)[label*="${dropdownItemFilter}"i]`);
    if (matchedDropdownItem) {
      this.focusDropdownItem(matchedDropdownItem);
    }
  }
  /**
   * Emits the values of the selected dropdown-items when the `valueState`-array changes
   */
  handleValueState(valueState) {
    emitEvent(this, 'clChange', { component, value: valueState }, valueState);
  }
  /**
   * Validates the maxHeight property and updates the styles accordingly
   */
  handleMaxHeight(maxHeight) {
    const cssVariable = '--custom-dropdown-max-height';
    switch (true) {
      case isUnset(maxHeight):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !isNumber(maxHeight):
        this.logger.error.attribute.maxHeight(`Given invalid value "${maxHeight}".`);
        break;
      default:
        updateElementStyle(this.element, numToRem(maxHeight * 4), cssVariable);
        break;
    }
  }
  /**
   * Updates the cl-dropdown element styles based on the `open` property
   */
  handleOpen(isOpen, wasOpen) {
    switch (true) {
      case isOpen && !wasOpen: {
        this.element.classList.remove('dropdown--closing');
        this.element.classList.remove('dropdown--closed');
        this.element.classList.add('dropdown--opening');
        this.isAnimating = true;
        break;
      }
      case !isOpen && wasOpen: {
        this.element.classList.remove('dropdown--opening');
        this.element.classList.remove('dropdown--opened');
        this.element.classList.add('dropdown--closing');
        this.isAnimating = true;
        break;
      }
      case !isOpen && !wasOpen: {
        this.element.classList.remove('dropdown--opened');
        this.element.classList.add('dropdown--closed');
        break;
      }
    }
  }
  /**
   * Validates the variant property
   */
  handleVariant(variant) {
    if (!dropdownValidator.variant(variant)) {
      this.logger.error.attribute.variant(`Given invalid value "${variant}".`);
      return;
    }
    const firstDropdownItem = this.getFirstDropdownItemElement();
    if (firstDropdownItem) {
      if (variant === dropdownConstants.VARIANT.INLINE) {
        firstDropdownItem.setAttribute('tabindex', '0');
      }
      else {
        firstDropdownItem.setAttribute('tabindex', '-1');
      }
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Validate the variant property;
   * - Update the max-height of the dropdown;
   * - Apply the open/close styling;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'change', 'clChange');
    logDeprecatedEventOnce(this.logger, 'closeEnd', 'clCloseEnd');
    logDeprecatedEventOnce(this.logger, 'closeStart', 'clCloseStart');
    logDeprecatedEventOnce(this.logger, 'openEnd', 'clOpenEnd');
    logDeprecatedEventOnce(this.logger, 'openStart', 'clOpenStart');
    this.handleVariant(this.variant);
    this.handleMaxHeight(this.maxHeight);
    if (this.variant !== dropdownConstants.VARIANT.INLINE) {
      this.handleOpen(this.open);
    }
  }
  /**
   * Just once after the component has fully loaded and rendered:
   * - Update the tabindex of the first dropdown-item element based on the variant property;
   * - Get the initially selected slotted element;
   */
  componentDidLoad() {
    this.handleVariant(this.variant);
    this.getInitialSelectedElements();
  }
  /**
   * Handles updating the states of the selected dropdown-items and values when a dropdown-item is selected
   */
  handleDropdownItemSelected(event) {
    if (event.target.nodeName === 'CL-DROPDOWN-ITEM') {
      const dropdownItem = event.target;
      const dropdownItemIsSelected = this.getSelectedState(dropdownItem);
      const { label: dropdownItemLabel, value: dropdownItemValue } = event.detail;
      switch (true) {
        case this.multiple:
          {
            let selectedDropdownItemsCopy = [...this.selectedDropdownItems];
            let valueStateCopy = [...this.valueState];
            if (dropdownItemIsSelected) {
              // Remove the element and its value from the `selectedDropdownItems` and `value` arrays
              selectedDropdownItemsCopy = selectedDropdownItemsCopy.filter(element => element !== dropdownItem);
              valueStateCopy = valueStateCopy.filter(({ label, value }) => value !== dropdownItemValue && label !== dropdownItemLabel);
              // Remove the elements' selected attribute
              this.unsetSelectedAttribute(dropdownItem);
            }
            else {
              // Add the element and its value to the `selectedDropdownItems` and `value` arrays
              selectedDropdownItemsCopy.push(dropdownItem);
              valueStateCopy.push(event.detail);
              // Add the elements' selected attribute
              this.setSelectedAttribute(dropdownItem);
            }
            this.selectedDropdownItems = selectedDropdownItemsCopy;
            this.value = valueStateCopy;
            this.valueState = valueStateCopy;
          }
          break;
        case !dropdownItemIsSelected:
          {
            this.selectedDropdownItems.forEach(this.unsetSelectedAttribute);
            this.setSelectedAttribute(dropdownItem);
            this.selectedDropdownItems = [dropdownItem];
            this.value = [event.detail];
            this.valueState = [event.detail];
          }
          break;
      }
      // Close the dropdown if the `closeOnSelect` attribute is true
      if (this.closeOnSelect) {
        this.open = false;
      }
    }
  }
  /**
   * Handles closing the dropdown when the user clicks outside of the dropdown and the `variant` property is not `inline`
   */
  handleWindowClick(event) {
    if (this.variant !== dropdownConstants.VARIANT.INLINE && this.open) {
      const hasClickedInsideDropdown = this.element.contains(event.target);
      if (!this.isAnimating && !hasClickedInsideDropdown) {
        this.open = false;
      }
    }
  }
  /**
   * Handles updating the currentFocusedElement constant when the dropdown is focused
   */
  handleFocusEvent(event) {
    if (event.target === this.element) {
      this.currentFocusedElement = event.target;
    }
  }
  /**
   * Function that `onKeydown` and when the dropdown element is focused:
   * - Updates the dropdown-item filter value when one of the allowed keys is pressed;
   * - Closes the dropdown when the escape key is pressed;
   * - Focuses the next/previous dropdown-item in the dropdown when the up/down key is pressed;
   */
  handleKeydownEvent(event) {
    var _a;
    const allowedRegex = /^[a-zA-Z0-9`~!@#$%^&*()-_=+[\]{}\\|;:'",<.>/?\s]{1}$/g;
    const isEscape = [event.key, event.code].includes('Escape');
    const isArrowUp = [event.key, event.code].includes('ArrowUp');
    const isArrowDown = [event.key, event.code].includes('ArrowDown');
    const isTab = [event.key, event.code].includes('Tab');
    switch (true) {
      // If one of the allowed keys is pressed, update the filter value (and reset the filter after 500ms of inactivity)
      case allowedRegex.test(event.key):
        event.preventDefault();
        this.dropdownItemFilter += event.key;
        this.resetDropdownItemFilter();
        break;
      // Close the dropdown when the escape key is pressed or the tab key is pressed while focused on a dropdown-item
      case isTab && ((_a = this.currentFocusedElement) === null || _a === void 0 ? void 0 : _a.nodeName) === 'CL-DROPDOWN-ITEM':
      case isEscape:
        this.open = false;
        break;
      // Focus the next/previous dropdown-item in the dropdown when the up/down key is pressed
      case isArrowUp:
      case isArrowDown: {
        event.preventDefault();
        event.stopPropagation();
        this.focusDropdownItem(null, isArrowUp ? 'up' : 'down');
      }
    }
  }
  /**
   * Public method for clearing the selected dropdown-items
   */
  async resetSelection() {
    this.selectedDropdownItems.forEach(this.unsetSelectedAttribute);
    this.value = [];
    this.valueState = [];
  }
  /**
   * Function that handles querying the dropdown-item element at index 0
   */
  getFirstDropdownItemElement() {
    return this.element.querySelector('cl-dropdown-item:not(.dropdown-item--disabled)');
  }
  /**
   * Function that returns whether the given dropdown-item is selected or not
   */
  getSelectedState(dropdownItemElement) {
    return !!this.selectedDropdownItems.includes(dropdownItemElement);
  }
  /**
   * Function that handles focusing on OR the given dropdown-item OR the next/previous dropdown-item in the dropdown
   */
  focusDropdownItem(dropdownItemElement, direction) {
    // If a dropdown-item is given to the function and it's not disabled, focus on it
    if (dropdownItemElement) {
      if (!dropdownItemElement.className.includes('disabled')) {
        this.currentFocusedElement = dropdownItemElement;
        focusElement(dropdownItemElement);
      }
    }
    else {
      // Else look for the next/previous dropdown-item in the dropdown based on the given direction and focus on it
      let nextDropdownItem = null;
      const allDropdownItems = Array.from(this.element.querySelectorAll('cl-dropdown-item:not(.dropdown-item--disabled)'));
      const currentActiveElement = document.activeElement;
      const currentIndex = allDropdownItems.findIndex(element => element === currentActiveElement);
      if (currentIndex > -1) {
        nextDropdownItem = currentIndex > -1 && allDropdownItems[currentIndex + (direction === 'up' ? -1 : 1)];
      }
      if (nextDropdownItem) {
        this.currentFocusedElement = nextDropdownItem;
        focusElement(nextDropdownItem);
      }
    }
  }
  /**
   * Handles adding the `selected` attribute to the given dropdown-item element
   */
  setSelectedAttribute(dropdownItemElement) {
    dropdownItemElement.setAttribute('selected', 'true');
  }
  /**
   * Handles removing the `selected` attribute from the given dropdown-item element
   */
  unsetSelectedAttribute(dropdownItemElement) {
    dropdownItemElement.removeAttribute('selected');
  }
  render() {
    const { fullWidth, variant } = this;
    return (h(Host, { class: cl('dropdown', {
        'dropdown--full-width': fullWidth,
        'dropdown--inline': variant === dropdownConstants.VARIANT.INLINE,
        'dropdown--menu': variant === dropdownConstants.VARIANT.MENU,
        'dropdown--opened': variant === dropdownConstants.VARIANT.INLINE,
      }), role: 'list', tabIndex: -1, onAnimationEnd: this.handleAnimationEvent, onAnimationStart: this.handleAnimationEvent }, h("cl-scroll-wrapper", { class: 'dropdown__wrapper', "thumb-only": true }, h("slot", null))));
  }
  get element() { return this; }
  static get watchers() { return {
    "dropdownItemFilter": ["handleDropdownItemFilter"],
    "valueState": ["handleValueState"],
    "maxHeight": ["handleMaxHeight"],
    "open": ["handleOpen"],
    "variant": ["handleVariant"]
  }; }
  static get style() { return clDropdownCss; }
}, [6, "cl-dropdown", {
    "autoFocus": [4, "auto-focus"],
    "closeOnSelect": [4, "close-on-select"],
    "fullWidth": [4, "full-width"],
    "maxHeight": [2, "max-height"],
    "multiple": [4],
    "open": [1028],
    "value": [1040],
    "variant": [1],
    "dropdownItemFilter": [32],
    "selectedDropdownItems": [32],
    "valueState": [32],
    "resetSelection": [64]
  }, [[2, "clSelect", "handleDropdownItemSelected"], [8, "click", "handleWindowClick"], [2, "focus", "handleFocusEvent"], [2, "keydown", "handleKeydownEvent"]]]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-dropdown", "cl-scroll-wrapper"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-dropdown":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClDropdown);
      }
      break;
    case "cl-scroll-wrapper":
      if (!customElements.get(tagName)) {
        defineCustomElement$1();
      }
      break;
  } });
}

export { ClDropdown as C, defineCustomElement as d };

//# sourceMappingURL=cl-dropdown2.js.map